import React from 'react'
import {useTranslation} from 'react-i18next'

import {Box} from '@mui/material'
import {
  getProductLineColors,
  PGSelectField,
  PGTypography,
  useProductLine,
} from '@progynyglobal/pg-ui-library'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import HTMLReactParser from 'html-react-parser'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as Chats} from '@progyny/assets/icons/chats.svg'
import {ReactComponent as PhoneOutline} from '@progyny/assets/icons/phone-outline.svg'
import {useMeQuery} from '@progyny/generated/graphql'
import type {EarliestSlot} from '@progyny/utils/calcom'

dayjs.extend(relativeTime)

export interface LanguageStepProps {
  value: string
  onChange: (value: string) => void
  earliestSlotForLanguage?: EarliestSlot | null
  earliestSelectedLanguageSlot?: EarliestSlot | null
}

const LanguageStep: React.FC<LanguageStepProps> = ({
  value,
  onChange,
  earliestSlotForLanguage,
  earliestSelectedLanguageSlot,
}) => {
  const {t} = useTranslation()
  const {productLine} = useProductLine()
  const theme = getProductLineColors(productLine)
  const {data: meQuery} = useMeQuery()
  const {classes} = useStyles({avatarBackground: theme.primaryLight})
  const languageOptions = (meQuery?.languages ?? []).flatMap((language) =>
    language ? [{value: language.code, label: language.name}] : []
  )
  const selectedLanguageLabel =
    languageOptions.find((option) => option.value === value)?.label ?? value
  const duration = earliestSelectedLanguageSlot
    ? dayjs(
        `${earliestSelectedLanguageSlot.date}T${earliestSelectedLanguageSlot.time}`
      ).fromNow(true)
    : undefined

  return (
    <Box className={classes.container}>
      <Box className={classes.avatarCircle}>
        <Chats color={theme.primary} />
      </Box>
      <PGTypography
        variant='header'
        textAlign='center'
        style={{width: 440, lineHeight: '32px'}}
      >
        {t('consultations_calendar_embed_language_step_heading')}
      </PGTypography>
      <PGTypography
        variant='caption'
        textAlign='center'
        color='#6b7280'
        style={{width: 440, fontWeight: 500}}
      >
        {t('consultations_calendar_embed_language_step_subtext')}
      </PGTypography>

      <Box className={classes.fieldGroup}>
        <PGTypography
          variant='subheader'
          style={{fontSize: 12, marginBottom: '8px'}}
        >
          {t('consultations_calendar_embed_language_step_label')}
        </PGTypography>
        <Box position='relative'>
          <Box className={classes.phoneIconWrapper}>
            <PhoneOutline color={theme.primary} />
          </Box>
          <PGSelectField
            options={languageOptions}
            value={value}
            onChange={(newValue) => onChange(String(newValue))}
            sx={{'& .MuiSelect-select': {paddingLeft: '44px'}}}
          />
        </Box>
        {value !== 'en' && earliestSlotForLanguage && (
          <Box
            data-testid='consultations-earlier-english-slot-card'
            px='14px'
            py='16px'
            mt='24px'
            borderRadius='12px'
            bgcolor={theme.navigatorBackgroundColor}
          >
            <PGTypography variant='caption'>
              {HTMLReactParser(
                duration
                  ? t(
                      'consultations_calendar_embed_language_step_earlier_date',
                      {
                        language: selectedLanguageLabel,
                        duration,
                      }
                    )
                  : t(
                      'consultations_calendar_embed_language_step_no_slot_in_language',
                      {language: selectedLanguageLabel}
                    )
              )}
            </PGTypography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const useStyles = makeStyles<{avatarBackground: string}>()(
  (theme, {avatarBackground}) => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px',
      padding: '32px',
    },
    avatarCircle: {
      width: '52px',
      height: '52px',
      marginTop: '24px',
      borderRadius: '99px',
      backgroundColor: avatarBackground,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    fieldGroup: {
      width: '420px',
      marginTop: '22px',
      marginBottom: '40px',
      gap: '24px',
    },
    phoneIconWrapper: {
      position: 'absolute',
      left: '16px',
      top: '50%',
      display: 'flex',
      transform: 'translateY(-50%)',
      zIndex: 1,
      pointerEvents: 'none',
    },
  })
)

export default LanguageStep
